import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./AireAcondicionado.css";

/* ============================================================
   API
============================================================ */

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER CATÁLOGO AIRE ACONDICIONADO
============================================================ */

const obtenerCatalogoAire = async () => {
  const respuesta = await axios.get(API_URL, {
    timeout: 45000,
  });

  return respuesta.data || {};
};

/* ============================================================
   COMPONENTE
============================================================ */

function AireAcondicionado() {
  /* ============================================================
     FILTROS
  ============================================================ */

  const [filtroSeleccionado, setFiltroSeleccionado] = useState("todos");

  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  /* ============================================================
     PAGINACIÓN
  ============================================================ */

  const [paginaActual, setPaginaActual] = useState(1);

  const productosPorPagina = 16;

  /* ============================================================
     CATÁLOGO
     TANSTACK QUERY + INDEXEDDB
  ============================================================ */

  const {
    data: catalogo = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["catalogo-aire-acondicionado"],
    queryFn: obtenerCatalogoAire,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (isError) {
    console.error("Error al obtener productos de aire acondicionado:", error);
  }

  /* ============================================================
     SCROLL AL CAMBIAR PÁGINA
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [paginaActual]);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  /* ============================================================
     BÚSQUEDA
  ============================================================ */

  const coincideBusqueda = (item) => {
    if (!filtroBusqueda.trim()) {
      return true;
    }

    const palabras = normalizarTexto(filtroBusqueda)
      .split(/\s+/)
      .filter(Boolean);

    const textoProducto = normalizarTexto(
      `${item?.nombre || ""} ${item?.marca || ""} ${
        item?.modelo || ""
      } ${item?.codigoProducto || ""} ${item?.descripcion || ""}`,
    );

    return palabras.every((palabra) => textoProducto.includes(palabra));
  };

  /* ============================================================
     CATEGORÍAS

     Ahora salen directamente del objeto almacenado por
     TanStack Query.
  ============================================================ */

  const categorias = useMemo(
    () => ({
      motor: catalogo.aireacondicionados || [],
      cinta: catalogo.cintas || [],
      rollo: catalogo.rollos || [],
      rubatex: catalogo.rubatexs || [],
      bomba: catalogo.bombacs || [],
      manguera: catalogo.manguerasAC || [],
    }),
    [catalogo],
  );

  /* ============================================================
     TODOS LOS PRODUCTOS
  ============================================================ */

  const todosLosProductos = useMemo(() => {
    return Object.values(categorias).flat();
  }, [categorias]);

  /* ============================================================
     PRODUCTOS FILTRADOS
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    const resultado = {};

    Object.entries(categorias).forEach(([categoria, productos]) => {
      resultado[categoria] = productos.filter(coincideBusqueda);
    });

    resultado.todos = todosLosProductos.filter(coincideBusqueda);

    return resultado;
  }, [categorias, todosLosProductos, filtroBusqueda]);

  /* ============================================================
     NOMBRES DE CATEGORÍAS
  ============================================================ */

  const nombresLegibles = {
    todos: "Todos los productos",
    motor: "Motores Rotativos",
    cinta: "Cintas",
    rollo: "Rollos de Cañería de Cobre",
    rubatex: "Rubatex",
    bomba: "Bombas de Condensado",
    manguera: "Mangueras",
  };

  /* ============================================================
     TÍTULOS
  ============================================================ */

  const titulosCategoria = {
    todos: "Componentes para Aire Acondicionado",
    motor: "Motores Rotativos",
    cinta: "Cintas para Aire Acondicionado",
    rollo: "Rollos de Cañería de Cobre",
    rubatex: "Rubatex",
    bomba: "Bombas de Condensación",
    manguera: "Mangueras",
  };

  /* ============================================================
     ICONOS
  ============================================================ */

  const iconosCategoria = {
    todos: "bi-grid",
    motor: "bi-fan",
    cinta: "bi-bounding-box",
    rollo: "bi-record-circle",
    rubatex: "bi-layers",
    bomba: "bi-droplet-half",
    manguera: "bi-bezier2",
  };

  /* ============================================================
     TÍTULO ACTUAL
  ============================================================ */

  const obtenerTituloCategoria = () =>
    titulosCategoria[filtroSeleccionado] || titulosCategoria.todos;

  /* ============================================================
     RESULTADOS
  ============================================================ */

  const productosCategoria = productosFiltrados[filtroSeleccionado] || [];

  const totalProductos = todosLosProductos.length;

  const productosMostrados = productosCategoria.length;

  /* ============================================================
     PRODUCTOS DE LA PÁGINA ACTUAL
  ============================================================ */

  const productosEnPagina =
    filtroSeleccionado === "todos"
      ? productosCategoria.slice(
          (paginaActual - 1) * productosPorPagina,
          paginaActual * productosPorPagina,
        )
      : productosCategoria;

  /* ============================================================
     TOTAL DE PÁGINAS
  ============================================================ */

  const totalPaginas =
    filtroSeleccionado === "todos"
      ? Math.ceil(productosCategoria.length / productosPorPagina)
      : 1;

  /* ============================================================
     CAMBIAR CATEGORÍA
  ============================================================ */

  const seleccionarCategoria = (categoria) => {
    setFiltroSeleccionado(categoria);
    setFiltroBusqueda("");
    setPaginaActual(1);
  };

  /* ============================================================
     CAMBIAR PÁGINA
  ============================================================ */

  const cambiarPagina = (pagina) => {
    if (pagina < 1 || pagina > totalPaginas) {
      return;
    }

    setPaginaActual(pagina);
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="aire-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="aire-hero">
          <div className="aire-container">
            <div className="aire-hero-card">
              <div>
                <span className="aire-kicker">CATÁLOGO G&amp;J FRÍO</span>

                <h1>{obtenerTituloCategoria()}</h1>

                <p>
                  Encuentra repuestos, accesorios y componentes para
                  instalación, mantenimiento y reparación de sistemas de aire
                  acondicionado.
                </p>
              </div>

              <div className="aire-hero-icon">
                <i className="bi bi-wind"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            TOOLBAR
        ===================================================== */}

        <section className="aire-toolbar-section">
          <div className="aire-container">
            <div className="aire-toolbar">
              <div className="aire-toolbar-left">
                <button
                  type="button"
                  className="aire-category-button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#aireCategories"
                  aria-controls="aireCategories"
                >
                  <i className="bi bi-grid-fill"></i>

                  <span>Categorías</span>

                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="aire-current-category">
                  <span>Categoría actual</span>

                  <strong>{nombresLegibles[filtroSeleccionado]}</strong>
                </div>
              </div>

              <div className="aire-counter">
                <span>Mostrando</span>

                <strong>{!isLoading ? productosMostrados : 0}</strong>

                <span>de</span>

                <strong>{!isLoading ? totalProductos : 0}</strong>

                <span>{totalProductos === 1 ? "artículo" : "artículos"}</span>
              </div>
            </div>

            {/* =================================================
                BUSCADOR
            ================================================= */}

            <div className="aire-search-row">
              <div className="aire-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, modelo o código..."
                  value={filtroBusqueda}
                  onChange={(e) => {
                    setFiltroBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                />

                {filtroBusqueda && (
                  <button
                    type="button"
                    className="aire-search-clear"
                    onClick={() => {
                      setFiltroBusqueda("");
                      setPaginaActual(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFCANVAS DE CATEGORÍAS
        ===================================================== */}

        <div
          className="offcanvas offcanvas-start aire-offcanvas"
          data-bs-scroll="true"
          tabIndex="-1"
          id="aireCategories"
          aria-labelledby="aireCategoriesLabel"
        >
          <div className="offcanvas-header aire-offcanvas-header">
            <div>
              <span>CATÁLOGO</span>

              <h5 className="offcanvas-title" id="aireCategoriesLabel">
                Subcategorías
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar"
            ></button>
          </div>

          <div className="offcanvas-body aire-offcanvas-body">
            <div className="aire-category-list">
              {Object.keys(nombresLegibles).map((categoria) => {
                const cantidad = productosFiltrados[categoria]?.length || 0;

                return (
                  <button
                    type="button"
                    key={categoria}
                    className={`aire-category-item ${
                      filtroSeleccionado === categoria ? "active" : ""
                    }`}
                    onClick={() => seleccionarCategoria(categoria)}
                    data-bs-dismiss="offcanvas"
                  >
                    <span className="aire-category-item-icon">
                      <i className={`bi ${iconosCategoria[categoria]}`}></i>
                    </span>

                    <span className="aire-category-item-text">
                      <strong>{nombresLegibles[categoria]}</strong>

                      <small>
                        {!isLoading ? cantidad : 0}{" "}
                        {cantidad === 1 ? "producto" : "productos"}
                      </small>
                    </span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            PRODUCTOS
        ===================================================== */}

        <section className="aire-products-section">
          <div className="aire-container">
            {/* =================================================
                SPINNER
            ================================================= */}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "100px",
                }}
              >
                <div
                  className="spinner-grow text-primary"
                  role="status"
                  style={{
                    width: "1.6rem",
                    height: "1.6rem",
                  }}
                >
                  <span className="visually-hidden">Cargando productos...</span>
                </div>
              </div>
            ) : productosEnPagina.length > 0 ? (
              /* ===============================================
                 PRODUCTOS
              =============================================== */

              <div className="aire-products-grid">
                {productosEnPagina.map((item) => (
                  <IndustrialProduct
                    key={item._id}
                    img={item.imagen}
                    title={item.nombre}
                    id={item._id}
                    details={[
                      {
                        label: "Marca",
                        value: item.marca,
                      },

                      ...(item.modelo
                        ? [
                            {
                              label: "Modelo",
                              value: item.modelo,
                            },
                          ]
                        : []),

                      {
                        label: "Código Producto",
                        value: item.codigoProducto,
                      },

                      {
                        label: "Descripción",
                        value: item.descripcion,
                      },
                    ]}
                  />
                ))}
              </div>
            ) : (
              /* ===============================================
                 SIN RESULTADOS
              =============================================== */

              <div className="aire-empty">
                <div className="aire-empty-icon">
                  <i className="bi bi-search"></i>
                </div>

                <h3>No encontramos productos</h3>

                <p>
                  No existen coincidencias para la búsqueda o categoría
                  seleccionada.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setFiltroBusqueda("");
                    setFiltroSeleccionado("todos");
                    setPaginaActual(1);
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Mostrar todos
                </button>
              </div>
            )}

            {/* =================================================
                PAGINACIÓN
            ================================================= */}

            {!isLoading &&
              filtroSeleccionado === "todos" &&
              totalPaginas > 1 && (
                <nav
                  className="aire-pagination-wrapper"
                  aria-label="Paginación de productos"
                >
                  {/* ANTERIOR */}

                  <button
                    type="button"
                    className="aire-pagination-arrow"
                    disabled={paginaActual === 1}
                    onClick={() => cambiarPagina(paginaActual - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>

                    <span>Anterior</span>
                  </button>

                  {/* NÚMEROS */}

                  <div className="aire-pagination-pages">
                    {Array.from(
                      {
                        length: totalPaginas,
                      },
                      (_, index) => index + 1,
                    ).map((pagina) => (
                      <button
                        type="button"
                        key={pagina}
                        className={`aire-page-button ${
                          paginaActual === pagina ? "active" : ""
                        }`}
                        onClick={() => cambiarPagina(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  {/* SIGUIENTE */}

                  <button
                    type="button"
                    className="aire-pagination-arrow"
                    disabled={paginaActual === totalPaginas}
                    onClick={() => cambiarPagina(paginaActual + 1)}
                  >
                    <span>Siguiente</span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </nav>
              )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default AireAcondicionado;
